/**
 * Shell design tokens (LOCKED-DESIGN 18.5 "restyled through a design-token
 * stylesheet shared with the shell", 18.8 code-owned appearance). Every color
 * is sourced from theme.ts — the single palette owner — and emitted as CSS
 * custom properties on :root, so the shell and the in-arena HUD (HudStyles.ts)
 * draw from the same signal-color taxonomy. src/shell/style.css owns structure
 * and layout and references these vars; no color literal lives in the CSS.
 *
 * The token names mirror the HUD stage (--tpz-teal / --tpz-amber / --tpz-red /
 * text tiers) so a teal here and a teal there mean the same thing.
 */
import {
  cssHex,
  cssRgba,
  FEEDBACK_COLORS,
  GRADE_BAND_HEX,
  PALETTE,
  RECEIVER_TIER_COLORS,
} from "@game/render/theme.js";

export const SHELL_TOKEN_STYLE_ID = "tpz-shell-tokens";

/**
 * Sanitation Grade band accents are owned by theme.ts (the single palette
 * owner) so the results-shell grade badge and the in-run HUD grade meter can
 * never drift apart. Re-exported here for shell consumers.
 */
export { GRADE_BAND_HEX };

/** The :root custom-property block, built from theme tokens. Injected once. */
export function shellRootCss(): string {
  return `:root {
  color-scheme: dark;
  --tpz-bg: #070910;
  --tpz-bg-deep: ${cssHex(PALETTE.background)};
  --tpz-panel: #12161f;
  --tpz-panel-hi: #171d28;
  --tpz-panel-solid: rgba(9, 12, 18, 0.96);
  --tpz-border: rgba(126, 148, 178, 0.22);
  --tpz-border-hi: rgba(126, 148, 178, 0.4);
  --tpz-text-hi: #eef2f8;
  --tpz-text: #d3dbe8;
  --tpz-text-mid: #c7d0e0;
  --tpz-text-dim: ${cssHex(FEEDBACK_COLORS.muted)};
  --tpz-teal: ${cssHex(PALETTE.conveyance)};
  --tpz-teal-deep: ${cssHex(0x1a6b52)};
  --tpz-blue: ${cssHex(PALETTE.anchorBlue)};
  --tpz-orange: ${cssHex(PALETTE.receiverOrange)};
  --tpz-amber: ${cssHex(FEEDBACK_COLORS.overflowWarning)};
  --tpz-red: ${cssHex(PALETTE.tierHazard)};
  --tpz-safe: ${cssHex(RECEIVER_TIER_COLORS.safe)};
  --tpz-technical: ${cssHex(RECEIVER_TIER_COLORS.technical)};
  --tpz-hazard: ${cssHex(RECEIVER_TIER_COLORS.hazard)};
  --tpz-grade-c: ${GRADE_BAND_HEX.C};
  --tpz-grade-b: ${GRADE_BAND_HEX.B};
  --tpz-grade-a: ${GRADE_BAND_HEX.A};
  --tpz-grade-s: ${GRADE_BAND_HEX.S};
  --tpz-stripe: ${cssRgba(PALETTE.receiverOrange, 0.9)};
  --tpz-stripe-dark: #14171f;
  --tpz-scrim: rgba(4, 6, 10, 0.74);
  --tpz-font: "Segoe UI", system-ui, -apple-system, "Helvetica Neue", Arial, sans-serif;
  --tpz-display: "Bahnschrift", "DIN Alternate", "Segoe UI Semibold", system-ui, sans-serif;
  --tpz-mono: ui-monospace, "Cascadia Code", "SF Mono", Menlo, monospace;
}`;
}

/** Inject (or refresh) the shell token block into <head>. */
export function installShellTokens(): void {
  if (typeof document === "undefined") return;
  let style = document.getElementById(SHELL_TOKEN_STYLE_ID) as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement("style");
    style.id = SHELL_TOKEN_STYLE_ID;
    document.head.appendChild(style);
  }
  style.textContent = shellRootCss();
}
